import { useEffect, useState } from 'react'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

interface Overview {
  companies: number
  providers: number
  missions: number
  totalRevenue: number
  missionsByStatus: Record<string, number>
}

function StatCard({ label, value, icon }: { label: string; value: string | number; icon: string }) {
  return (
    <div className="glass-card p-6">
      <div className="flex items-start justify-between mb-3">
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="text-3xl font-bold text-text-primary mb-1">{value}</p>
      <p className="text-text-secondary text-sm">{label}</p>
    </div>
  )
}

export default function AdminPage() {
  const [overview, setOverview] = useState<Overview | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const token = localStorage.getItem('velink_token')
    fetch(`${API}/api/admin/overview`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('Non autorisé')
        return r.json()
      })
      .then(setOverview)
      .catch((e) => setError(e.message))
  }, [])

  if (error) return (
    <div className="flex items-center justify-center h-64">
      <div className="glass-card p-6 text-danger text-center">
        <p className="font-semibold mb-2">Accès refusé</p>
        <p className="text-sm text-text-secondary">{error}</p>
      </div>
    </div>
  )

  if (!overview) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-text-muted animate-pulse">Chargement…</div>
    </div>
  )

  const statusLabels: Record<string, string> = {
    OPEN: 'Ouvertes', ASSIGNED: 'Assignées', IN_PROGRESS: 'En cours',
    COMPLETED: 'Terminées', CANCELLED: 'Annulées',
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary">Vue d'ensemble</h1>
        <p className="text-text-secondary mt-1">Tableau de bord administrateur Velink</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <StatCard icon="🏢" label="Entreprises" value={overview.companies} />
        <StatCard icon="👤" label="Prestataires" value={overview.providers} />
        <StatCard icon="📋" label="Missions totales" value={overview.missions} />
        <StatCard icon="💶" label="Revenus capturés" value={`${(overview.totalRevenue / 100).toFixed(0)} €`} />
      </div>

      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Missions par statut</h2>
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {Object.entries(overview.missionsByStatus).map(([status, count]) => (
            <div key={status} className="glass-card-dark p-4 rounded-xl text-center">
              <p className="text-2xl font-bold text-text-primary">{count}</p>
              <p className="text-text-muted text-xs mt-1">{statusLabels[status] ?? status}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
